/* eslint-disable no-nested-ternary */
import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { FC, useState } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useSchool, useSdk } from "@app/shared/lib/hooks";
import { DateTime, HelpText, Loc, Price, Table } from "@app/shared/ui";

export const OrderTable: FC = () => {
  const { partnership } = useSchool();
  const sdk = useSdk();
  const [isEmpty, setEmpty] = useState(false);

  return isEmpty ? (
    <HelpText
      label={
        <Loc word="Partnership.order.empty" default="No orders available" />
      }
      className="fs-14"
    />
  ) : (
    <Table
      defaultFilters={{ take: 25 }}
      fetchItemsByFilter={async (filters) => {
        const res = await sdk.partnership.orderStore.fetch({
          ...filters,
          sortType: filters.sortType === SortingDirection.asc ? "Asc" : "Desc",
        });

        if (res.body.items.length === 0) {
          setEmpty(true);
        }

        return {
          items: res.body.items,
          filters: {
            ...res.body.filter,
            sortType:
              res.body.filter.sortType === "Desc"
                ? SortingDirection.desc
                : SortingDirection.asc,
            pageTotal: sdk.partnership.orderStore.filter.pageTotal,
            page: sdk.partnership.orderStore.filter.page,
          },
        };
      }}
      rowBordered={false}
      columns={[
        {
          title: <Loc word="PurchaseOrder.dateLabel" default="Order date" />,
          key: "date",
          width: "20%",
          render: (_text, item) => (
            <DateTime date={item.createdDate} format="l" />
          ),
        },
        {
          title: <Loc word="PurchaseOrder.numberLabel" default="Number" />,
          key: "number",
          width: "20%",
          render: (_text, item) => <>#{item.number}</>,
        },
        {
          title: <Loc word="PurchaseOrder.amountLabel" default="Amount" />,
          key: "amount",
          width: "20%",
          align: "right",
          render: (_text, item) => (
            <>
              <Price price={item.totalAmount} />

              {(item.paidBonusAmount > 0 || item.feeAmount > 0) && (
                <Tooltip
                  title={
                    <span className="flex flex-col">
                      {item.paidBonusAmount > 0 && (
                        <span>
                          <Loc
                            word="PurchaseOrderPayment.type.bonus"
                            default="Bonus amount"
                          />
                          :&nbsp;
                          <Price price={item.paidBonusAmount} />
                        </span>
                      )}
                      {item.feeAmount > 0 && (
                        <span>
                          <Loc
                            word="PurchaseOrder.gatewayFeeAmount"
                            default="Payment fee amount"
                          />
                          :&nbsp;
                          <Price price={item.feeAmount} />
                        </span>
                      )}
                    </span>
                  }
                  className="ml-5"
                >
                  <InfoCircleOutlined className="color-primary" />
                </Tooltip>
              )}
            </>
          ),
        },
        {
          title: (
            <Loc word="PurchaseOrder.paidAmountLabel" default="Paid amount" />
          ),
          key: "paidAmount",
          width: "20%",
          align: "right",
          render: (_text, item) => <Price price={item.paidAmount} />,
        },
        {
          title: <Loc word="Global.status" default="Status" />,
          key: "status",
          width: "20%",
          render: (_text, item) =>
            item.fullyPaid ? (
              <span>
                <CheckOutlined className="color-success" />
                &nbsp;
                <Loc word="PurchaseOrder.status.paid" default="Paid" />
              </span>
            ) : item.paidAmount > 0 ? (
              <span>
                <CheckOutlined className="color-warning" />
                &nbsp;
                <Loc
                  word="PurchaseOrder.status.partiallyPaid"
                  default="Partially paid"
                />
              </span>
            ) : (
              <span>
                <CloseOutlined className="color-danger" />
                &nbsp;
                <Loc word="PurchaseOrder.status.notPaid" default="Not paid" />
              </span>
            ),
        },
        {
          title: <Loc word="PurchaseOrder.contactLabel" default="Contact" />,
          key: "contact",
          width: "20%",
          render: (_text, item) => (
            <div className="lh-18">
              <div>{item.contact?.fullNameNoEmail}</div>
              {partnership?.partnershipOrdersContactVisible === true && (
                <>
                  {item.contact?.email && <div>{item.contact?.email}</div>}
                  {item.contact?.phone && <div>{item.contact?.phone}</div>}
                </>
              )}
            </div>
          ),
        },
      ]}
      hidePaginationOnePage
      className="partnership-table"
      size="small"
      style={{ maxWidth: 1280 }}
    />
  );
};
